import { useEffect, useState, lazy, Suspense } from "react";
import Header from "@/components/landing/Header";
import Hero from "@/components/landing/Hero";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import SectionFX from "@/components/landing/SectionFX";
import LazyMount from "@/components/landing/LazyMount";
import Preloader from "@/components/landing/premium/Preloader";

// Perf : tout ce qui est sous le Hero est chargé en chunks séparés => bundle initial minimal (FCP/LCP/TBT)
const AppShowcase = lazy(() => import("@/components/landing/AppShowcase"));
const VoiceShowcase = lazy(() => import("@/components/landing/VoiceShowcase"));
const WidgetShowcase = lazy(() => import("@/components/landing/widget/WidgetShowcase"));
const WhyDriveFlow = lazy(() => import("@/components/landing/WhyDriveFlow"));
const Features = lazy(() => import("@/components/landing/Features"));
const Founder = lazy(() => import("@/components/landing/Founder"));
const Pricing = lazy(() => import("@/components/landing/Pricing"));
const Security = lazy(() => import("@/components/landing/Security"));
const FaqAndApps = lazy(() => import("@/components/landing/FaqAndApps"));
const Footer = lazy(() => import("@/components/landing/Footer"));
const MarqueeBand = lazy(() => import("@/components/landing/premium/MarqueeBand"));
const LeadDialog = lazy(() => import("@/components/landing/LeadDialog"));
const Toaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

// Section différée : placeholder (anti-CLS) -> montage à l'approche du viewport / file idle -> chunk lazy
const Deferred = ({ children, minHeight }) => (
  <LazyMount minHeight={minHeight}>
    <Suspense fallback={<div className="bg-[#0A0A0A]" style={{ minHeight: minHeight || "100vh" }} aria-hidden="true" />}>{children}</Suspense>
  </LazyMount>
);

export default function Landing() {
  const [dialog, setDialog] = useState({ open: false, plan: "trial", source: "hero", pack: null, mounted: false });

  // Deep-link (#tarifs, #faq…) : la cible peut être montée après le load, on attend qu'elle existe
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    let tries = 0;
    const id = setInterval(() => {
      const el = document.querySelector(hash);
      tries += 1;
      if (el || tries > 30) {
        clearInterval(id);
        el?.scrollIntoView({ block: "start" });
      }
    }, 250);
    return () => clearInterval(id);
  }, []);

  const openLead = (plan = "trial", source = "generic", pack = null) => {
    setDialog({ open: true, plan, source, pack, mounted: true });
  };

  return (
    <div className="App font-body bg-[#0A0A0A]" data-testid="landing-page">
      <Preloader />
      <AnnouncementBar />
      <Header onCta={() => openLead("trial", "header")} />
      <main>
        <SectionFX>
          <Hero onCta={() => openLead("trial", "hero")} />
        </SectionFX>
        <Deferred>
          <SectionFX>
            <AppShowcase onCta={() => openLead("trial", "app-showcase")} />
          </SectionFX>
        </Deferred>
        <Deferred>
          <SectionFX>
            <VoiceShowcase />
          </SectionFX>
        </Deferred>
        <Deferred minHeight="200vh">
          <WidgetShowcase />
        </Deferred>
        <Deferred>
          <SectionFX>
            <WhyDriveFlow />
          </SectionFX>
        </Deferred>
        <Deferred minHeight="150vh">
          <SectionFX cvAuto>
            <Features />
          </SectionFX>
        </Deferred>
        <Deferred>
          <SectionFX cvAuto>
            <Founder />
          </SectionFX>
        </Deferred>
        <Deferred minHeight="120px">
          <MarqueeBand />
        </Deferred>
        <Deferred minHeight="150vh">
          <SectionFX cvAuto>
            <Pricing
              onMonthly={() => openLead("monthly", "pricing")}
              onTokens={(pack) => openLead("tokens", pack ? `pricing-pack-${pack.tokens}` : "pricing", pack)}
            />
          </SectionFX>
        </Deferred>
        <Deferred minHeight="60vh">
          <Security />
        </Deferred>
        <Deferred>
          <FaqAndApps onCta={() => openLead("trial", "faq")} />
        </Deferred>
      </main>
      <Deferred minHeight="80vh">
        <Footer onCta={(plan, source) => openLead(plan, source)} />
      </Deferred>
      <Suspense fallback={null}>
        <Toaster position="top-center" richColors closeButton />
        {dialog.mounted && (
          <LeadDialog
            open={dialog.open}
            plan={dialog.plan}
            source={dialog.source}
            pack={dialog.pack}
            onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))}
          />
        )}
      </Suspense>
    </div>
  );
}
